import { useState, useEffect, useRef, useCallback } from "react";
import tw from "tailwind-styled-components";
import { useMutation } from "react-query";
import axios from "axios";
import Config from "../../Config";
import Loading from "../../Components/Loading";
import { toast } from "react-toastify";
import {
    BoxContainer,
    BoxTitle,
    Underline,
    BoxHead,
    FilterTableWrapper,
    FilterWrapperWithSearch,
} from "../../Components/Styles/PageStyles";
import NotFoundModel from "../../Components/NotFoundModel";
import { useSelector } from "react-redux";
import moment from "moment";
import { SummarySkeletonLoading } from "../../Components/SkeletonLoading";
import DateRange from "../../Components/DateRange";
import PropertiesFilterDropdown from "../../Components/PropertiesFilterDropdown";
import SummaryCards from "../../Components/LandlordPortal/Dues/SummaryCards";
import DuesTable from "../../Components/LandlordPortal/Dues/Table";

const LandlordDues = () => {
    const properties = useSelector((state) => state.PropertyReducer.property);
    const user = useSelector((state) => state.UserReducer.user);
    const isFirstLoad = useRef(true);
    const property = useSelector(
        (state) => state.SelectPropertyReducer.selectProperty
    );
    const [isFocused, setIsFocused] = useState(false);
    const [selectedData, setSelectedData] = useState("");
    const [filter, setFilter] = useState("");
    const [apiData, setApiData] = useState();
    const [selectedProperty, setSelectedProperty] = useState(property);

    const [summary, setSummary] = useState({
        lifetimeTotal: 0,
        rent: 0,
        security: 0,
        other: 0,
    });
    const [startDate, setStartDate] = useState(
        moment().startOf("month").format("YYYY-MM-DD")
    );

    const [endDate, setEndDate] = useState(
        moment().endOf("month").format("YYYY-MM-DD")
    );

    const getFetchFunction = async () => {
        const query = `&startDate=${startDate}&endDate=${endDate}&propId=${selectedProperty?.id}&filter=${filter}`;
        const response = await axios.get(`${Config.apiUrl}/landlord/list/dues?${query}`, {
            headers: {
                authorization: `Bearer ${user.token}`,
                "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
            },
        });
        return response.data;
    };

    const getSuccess = (res) => {
        if (res.isSuccess === false) {
            toast.error(res.msg || "An error occurred");
            return;
        } else {
            setApiData(res?.list || []);
            setSummary(res?.summary || {
                lifetimeTotal: 0,
                rent: 0,
                security: 0,
                other: 0,
            });

            setIsFocused(true);
        }
    };

    const getError = (res) => {
        toast.error(res.message || "Failed to fetch dues");
    };

    const {
        isLoading,
        mutate: getListMutate,
        error,
        isRefetching,
    } = useMutation(getFetchFunction, {
        onSuccess: getSuccess,
        onError: getError,
    });

    const stableGetListMutate = useCallback(() => {
        getListMutate();
    }, [getListMutate]);

    useEffect(() => {
        if (isFirstLoad.current) {
            isFirstLoad.current = false;
            stableGetListMutate();
            return;
        }

        const timer = setTimeout(() => {
            stableGetListMutate();
        }, 300);

        return () => clearTimeout(timer);
    }, [
        filter,
        selectedProperty?.id,
        startDate,
        endDate,
        stableGetListMutate,
    ]);

    useEffect(() => {
        if (startDate) sessionStorage.setItem("due_startDate", startDate);
        if (endDate) sessionStorage.setItem("due_endDate", endDate);
    }, [startDate, endDate]);

    const submitHandler = (values) => {
        const newValues = {
            startDate: moment(values.startDate).format("YYYY-MM-DD"),
            endDate: moment(values.endDate).format("YYYY-MM-DD"),
        };
        setFilter("");
        setStartDate(newValues.startDate);
        setEndDate(newValues.endDate);
    };

    const onSummaryBoxClick = (data) => {
        sessionStorage.removeItem("tenant_dues_page");
        sessionStorage.setItem("tenant_dues_filter", data);
        setTimeout(() => {
            setFilter(data);
        }, 50);
    };

    const handlePropertySelect = (property) => {
        sessionStorage.removeItem("tenant_dues_page");
        setSelectedProperty(property);
    };

    return (
        <>
            <BoxContainer>
                <BoxHead>
                    <BoxTitle className="!mb-0 whitespace-nowrap">
                        Dues
                        <Underline />
                    </BoxTitle>
                </BoxHead>

                {(isLoading || isRefetching) && !isFocused && (
                    <SummarySkeletonLoading count={5} />
                )}
                {isFocused && summary && (
                    <>
                        <SummaryCards summaryData={summary} onClick={onSummaryBoxClick} />
                        <FilterTableWrapper>
                            <FilterWrapperWithSearch >
                                <div className="flex flex-col md:flex-row md:items-center lg:space-x-4 md:gap-0 gap-4">
                                    <DateRange
                                        submitHandler={submitHandler}
                                        defaultLabel={
                                            moment(startDate).format("DD MMM yyyy") +
                                            " - " +
                                            moment(endDate).format("DD MMM yyyy")
                                        }
                                    />
                                </div>
                                <div
                                    className="flex gap-2 justify-end items-center pr-4"
                                >
                                    <PropertiesFilterDropdown
                                        properties={properties}
                                        selectedProperty={selectedProperty}
                                        onPropertySelect={handlePropertySelect}
                                        placeholder="Select Property"
                                        className="min-w-[200px] w-full md:w-[220px]"
                                    />
                                </div>
                            </FilterWrapperWithSearch>
                            {!error && !isLoading && apiData && (
                                <TableWrapper>
                                    <DuesTable
                                        apiData={apiData}
                                        setSelectedData={setSelectedData}
                                        selectedData={selectedData}
                                    />
                                </TableWrapper>
                            )}
                            {!error && !isLoading && apiData && !apiData.length && (
                                <NotFoundModel msg={"No Dues Pending"} />
                            )}
                        </FilterTableWrapper>
                    </>
                )}
                {error && !isLoading && <NotFoundModel />}
                {(isLoading || isRefetching) && <Loading />}
            </BoxContainer>
        </>
    );
};

const TableWrapper = tw.div`mt-4 bg-white rounded-md `;
export default LandlordDues;
